import { useOfficeFile, useScreen } from "hooks";
import { startTransition, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Renders Word, Excel and PowerPoint documents in the Microsoft Office web
 * viewer.
 *
 * The viewer fetches the document from MICROSOFT'S servers, so it can no longer
 * be handed a storage URL: files are now readable only through the node that
 * references them, and the presigned URL that grants access lives 30 seconds —
 * long enough for this browser, never for a third party fetching later and
 * re-fetching on every render. It is handed a backend link instead, which the
 * backend mints once it has confirmed this user may read the file, and serves
 * the bytes on for a couple of hours.
 */
const OfficeViewer = ({ fileId }: { fileId?: string }) => {
	const { t } = useTranslation();
	const file = useOfficeFile(fileId);
	const screen = useScreen();
	const [height, setHeight] = useState("0px");

	useEffect(() => {
		const scroll =
			document.querySelector("#scroll") ?? document.scrollingElement;
		startTransition(() => {
			setHeight(`${(scroll?.scrollHeight ?? 0) - (screen ? 100 : 210)}px`);
		});
	}, [screen]);

	// `null` is a refusal (no access, or the backend is down), `undefined` is
	// still signing.
	if (file === null)
		return <p>{t("common.noPreview", "No preview available for this file")}</p>;
	if (!file) return null;

	return (
		<iframe
			title="MsOfficeViewer"
			width="100%"
			height={height}
			frameBorder="0"
			src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
				file,
			)}`}
		/>
	);
};

export default OfficeViewer;
